import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import { acceptAndSendContract, fetchAcceptAndSendPreview } from '@src/api/actions/prospects';

const fmtCurrency = (n) => (n != null ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—');

/**
 * Confirm step for the one-click "Accept & send contract": converts the
 * prospect, generates the contract from the default template and queues the
 * signing email. Shows fee, verticals, budget, signer and template first;
 * anything the server can't fill sends staff on to ContractBuilder.
 */
export default function AcceptAndSendContractDialog({ token, open, onClose, onDone }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendBooking, setSendBooking] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    setPreview(null);
    setSendBooking(false);
    setLoading(true);
    fetchAcceptAndSendPreview(token)
      .then((res) => setPreview(res?.data || null))
      .catch(() => toast.error('Failed to load contract preview'))
      .finally(() => setLoading(false));
  }, [open, token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await acceptAndSendContract(token, { sendBookingEmail: sendBooking });
      const data = res?.data || {};
      const delivery = data.lead_delivery;
      if (delivery?.mode === 'staging') {
        toast.warning('Lead webhook copied in STAGING: finish it on the Webhook Setup page.');
      }
      if (data.outcome === 'sent') {
        toast.success(data.detail || 'Contract sent');
      } else if (data.outcome === 'contract_exists') {
        toast.info(data.detail);
      } else {
        toast.error(data.detail || 'Finish the contract in ContractBuilder');
      }
      onDone?.(data);
      onClose();
      if (data.client_slug) {
        const query = data.outcome === 'needs_review' ? '?openContract=1' : '';
        navigate(`${paths.clientHub(data.client_slug)}${query}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept and send contract');
    } finally {
      setSubmitting(false);
    }
  };

  const missing = preview?.missing || [];
  const warnings = preview?.warnings || [];
  const existing = preview?.existing_contract;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Accept &amp; send contract</DialogTitle>
      <DialogContent>
        {loading || !preview ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {preview.client_slug
                ? 'This prospect is already a client. '
                : 'Creates the client from this prospect, then '}
              generates the contract and emails the signing link.
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 140 }}>Client</TableCell>
                  <TableCell>{preview.company_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Fee</TableCell>
                  <TableCell>{preview.fee_pct != null ? `${preview.fee_pct}% of ad spend` : '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Verticals</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(preview.verticals || []).map((v) => (
                        <Chip key={v} size="small" variant="outlined"
                          label={preview.vertical_budgets?.[v] ? `${v} · ${fmtCurrency(preview.vertical_budgets[v])}` : v} />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Budget</TableCell>
                  <TableCell>{fmtCurrency(preview.budget)} / month</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Signer</TableCell>
                  <TableCell>
                    {preview.signer_name || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {preview.signer_email || 'no email'}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Template</TableCell>
                  <TableCell>{preview.template?.name || '—'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {existing && (
              <Alert severity="info" variant="outlined">
                A contract already exists ({existing.sign_status}). Nothing new will be created or sent.
              </Alert>
            )}
            {missing.length > 0 && !existing && (
              <Alert severity="warning">
                The client will be created, but the contract will NOT be sent automatically:
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
                You will land in ContractBuilder to finish it.
              </Alert>
            )}
            {warnings.length > 0 && (
              <Alert severity="info" variant="outlined">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </Alert>
            )}
            {!preview.client_slug && (
              <FormControlLabel
                control={<Checkbox checked={sendBooking} onChange={(e) => setSendBooking(e.target.checked)} size="small" />}
                label="Also email the discovery-call booking link"
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={submitting || loading || !preview}
        >
          {submitting ? 'Working...' : missing.length && !existing ? 'Create client' : 'Accept & send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
